import React, { useCallback, useEffect, useRef } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Form } from "antd";
import styled from "styled-components";
import { useSlugChecker } from "pages/media/projects/hooks/useSlugChecker";
import { slugify } from "utils/text";
import { radius } from "@libertify/ui";

export interface SlugSectionProps {
  /** The instance's currently saved slug, if any. */
  initialSlug?: string;
  /** The experience's title — used to suggest a slug when there's no saved
   *  one yet (mirrors `ProjectSlugSelection`'s title-based suggestion). */
  title?: string;
  organizationSlug?: string;
  /** The instance fetch is still in flight — keeps the field disabled and
   *  holds off the title-suggestion below, so it never shows an
   *  empty/generated slug for one that just hasn't loaded yet. */
  isLoading?: boolean;
  /** Fired on blur with a validated, available slug that differs from
   *  `initialSlug`. The caller owns persistence (PATCH + markAsOutOfDate) —
   *  this component only ever reports a slug it has confirmed is available. */
  onCommit: (slug: string) => void;
}

/**
 * Slug field for the experience edit page (MED-6958). Deliberately NOT
 * `ProjectSlugSelection` — that component is shared with `ProjectPublish`,
 * carries a web of effects reconciling several pieces of lifted state, and
 * has a couple of real bugs in that reconciliation (stomping a newly-typed,
 * confirmed-available slug back to the original on blur, saving on every
 * keystroke instead of on blur). Editing it risks the publish flow it's
 * shared with, so this is a small purpose-built field instead: local state
 * only, one `onCommit` call out, on blur, same shape as the Name/Description
 * fields' `onFieldBlur` in `ExperienceDetailsSection`.
 *
 * The markup/styling below mirrors `ProjectSlugSelection`'s (same "…/" prefix,
 * border coloring, availability row, char count) so the two don't look like
 * different components — just duplicated rather than shared, since touching
 * that component isn't an option here.
 *
 * No auto-generated slug suggestion and no alternate-slug suggestion here —
 * out of scope for this pass. An instance with no slug yet just shows an
 * empty field.
 */
export const SlugSection: React.FC<SlugSectionProps> = ({
  initialSlug,
  title,
  organizationSlug,
  isLoading: isInstanceLoading,
  onCommit,
}) => {
  const t = useTranslate();

  const checkEndpoint = useCallback(
    (slug: string) =>
      `media/organizations/${organizationSlug}/instances/${slug}/availability`,
    [organizationSlug]
  );

  const {
    form,
    slug,
    isSlugAvailable,
    isSlugUnavailable,
    hasChecked,
    handleCheck,
    handleSlugChange,
    handleKeyPress,
  } = useSlugChecker({
    checkEndpoint,
    initialSlug,
    // Seeds the field once `initialSlug` actually loads — separate from
    // `initialSlug` itself, which only drives the "already known good"
    // shortcut inside `handleCheck`.
    initialValue: initialSlug,
  });

  // No saved slug yet — suggest one from the title, same as
  // `ProjectSlugSelection`'s `checkAndSetTitleAsSlug`. Guarded by a ref
  // (not `handleCheck`/`form` identity) so it only ever runs once: the
  // original's version of this re-ran on every render because it depended
  // on those non-memoized identities directly, which is what turned it into
  // an effect that kept re-firing.
  const hasSuggestedRef = useRef(false);
  useEffect(() => {
    if (
      isInstanceLoading ||
      initialSlug ||
      hasSuggestedRef.current ||
      !title?.trim()
    ) {
      return;
    }
    hasSuggestedRef.current = true;
    const suggested = slugify(title);
    if (!suggested) return;
    void handleCheck(suggested).then(({ isAvailable }) => {
      if (isAvailable) form.setFieldValue("slug", suggested);
    });
    // `handleCheck`/`form` deliberately excluded — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstanceLoading, initialSlug, title]);

  const handleBlur = () => {
    const trimmed = slug.trim();
    if (!trimmed || trimmed === initialSlug) return;
    void handleCheck(trimmed).then(({ isAvailable }) => {
      if (isAvailable) onCommit(trimmed);
    });
  };

  return (
    <Flex vertical gap={2}>
      <StyledUrlBuilder
        $hasChecked={hasChecked && slug.trim().length > 0}
        $isAvailable={isSlugAvailable && slug.trim().length > 0}
      >
        <StyledSlashPrefix>… /</StyledSlashPrefix>
        {/* `Form.useWatch` only reflects a path that has an actual
            registered Field — with no `Form.Item` mounted, `getFieldsValue()`
            (which the watch reads from) always came back empty regardless of
            `form.setFieldValue`, so `slug` never left "". This Form.Item is
            required, not decorative. */}
        <Form form={form} component={false}>
          <Form.Item
            name="slug"
            noStyle
            normalize={(value: string | undefined) => value?.trimEnd()}
          >
            <StyledUrlSlugInput
              onChange={handleSlugChange}
              onKeyDown={handleKeyPress}
              onBlur={handleBlur}
              placeholder={t(
                "media.projects.share.ChooseUrl.pageNamePlaceholder"
              )}
              // Not gated on `isChecking` — disabling a focused input forces
              // a native blur before the in-flight check resolves, which
              // races `handleBlur`'s own check for the same slug and can
              // silently drop the commit.
              disabled={isInstanceLoading}
              maxLength={255}
            />
          </Form.Item>
        </Form>
      </StyledUrlBuilder>

      <Flex justify="space-between" align="flex-start">
        <div>
          {!slug && (
            <StyledAvailabilityRow $ok={false}>
              <StyledAvailDot $ok={false} />
              {t("components.UrlSlugChecker.slugEmpty")}
            </StyledAvailabilityRow>
          )}
          {slug && isSlugUnavailable && (
            <StyledAvailabilityRow $ok={false}>
              <StyledAvailDot $ok={false} />
              {t("components.UrlSlugChecker.slugNotAvailable")}
            </StyledAvailabilityRow>
          )}
          {isSlugAvailable && (
            <StyledAvailabilityRow $ok={true}>
              <StyledAvailDot $ok={true} />
              {t("components.UrlSlugChecker.slugAvailable")}
            </StyledAvailabilityRow>
          )}
        </div>
        <StyledSlugCharCount>{slug?.length || 0}/255</StyledSlugCharCount>
      </Flex>
    </Flex>
  );
};

export default SlugSection;

// ─── Styled components (mirrors ProjectSlugSelection's presentation) ──────

const StyledUrlBuilder = styled.div<{
  $hasChecked: boolean;
  $isAvailable: boolean;
}>`
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1.5px solid
    ${(p) =>
      p.$hasChecked ? (p.$isAvailable ? "#059669" : "#ef4444") : "#e5e7eb"};
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${(p) =>
      p.$hasChecked ? (p.$isAvailable ? "#059669" : "#ef4444") : "#14b8a6"};
  }
`;

const FONT_INTER = '"Inter Variable", "Inter", sans-serif';

const StyledSlashPrefix = styled.span`
  flex-shrink: 0;
  padding-left: 12px;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #9ca3af;
`;

const StyledUrlSlugInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #111827;
  font-weight: 600;
  padding: 10px 6px;
  outline: none;
  min-width: 0;
`;

const StyledAvailabilityRow = styled.div<{ $ok: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: ${FONT_INTER};
  font-size: 12px;
  color: ${(p) => (p.$ok ? "#059669" : "#ef4444")};
`;

const StyledAvailDot = styled.span<{ $ok: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: ${radius.circle};
  background: ${(p) => (p.$ok ? "#059669" : "#ef4444")};
  flex-shrink: 0;
`;

const StyledSlugCharCount = styled.span`
  font-family: ${FONT_INTER};
  font-size: 11px;
  color: #9ca3af;
  padding: 0 4px;
`;
